import { useBackendAvailable } from '@affine/core/components/hooks/use-backend-availability';
import { WorkspacePermissionService } from '@affine/core/modules/permissions';
import { useI18n } from '@affine/i18n';
import { GroupIcon, PageIcon, UserIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { SettingsOfflineHeaderAction } from '../../shared/settings-offline-status';
import { SettingsEntityMessage } from '../shared/settings-entity';
import { SettingsSearchableEntityPage } from '../shared/settings-entity-page';
import { SettingsViewModeMenu } from '../shared/settings-navigation';
import { DocListView } from './doc-list-view';
import { GroupListView } from './group-list-view';
import { UserListView } from './user-list-view';

type ViewMode = 'docs' | 'users' | 'groups';

export const PermissionsSettingsPanel = () => {
  const t = useI18n();
  const backendAvailable = useBackendAvailable();
  const permissionService = useService(WorkspacePermissionService);

  const isOwner = useLiveData(permissionService.permission.isOwner$);
  const isAdmin = useLiveData(permissionService.permission.isAdmin$);
  const canManage = isOwner || isAdmin;

  const [viewMode, setViewMode] = useState<ViewMode>('docs');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    permissionService.permission.revalidate();
  }, [permissionService]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  const title = useMemo(
    () =>
      t['com.streamlain.settings.workspace.permissions.title']?.() ??
      'Permissions',
    [t]
  );

  const desc = useMemo(
    () =>
      t['com.streamlain.settings.workspace.permissions.description']?.() ??
      'Manage document and folder permissions for workspace members.',
    [t]
  );

  const viewModeLabel = useMemo(() => {
    switch (viewMode) {
      case 'docs':
        return (
          t['com.streamlain.settings.workspace.permissions.by-document']?.() ??
          'By Document'
        );
      case 'users':
        return (
          t['com.streamlain.settings.workspace.permissions.by-user']?.() ??
          'By User'
        );
      case 'groups':
        return (
          t['com.streamlain.settings.workspace.permissions.by-group']?.() ??
          'By Group'
        );
    }
  }, [viewMode, t]);

  const searchPlaceholder = useMemo(() => {
    switch (viewMode) {
      case 'docs':
        return (
          t['com.streamlain.settings.workspace.permissions.search-docs']?.() ??
          'Search documents...'
        );
      case 'users':
        return (
          t['com.streamlain.settings.workspace.permissions.search-users']?.() ??
          'Search users...'
        );
      case 'groups':
        return (
          t[
            'com.streamlain.settings.workspace.permissions.search-groups'
          ]?.() ?? 'Search groups...'
        );
    }
  }, [viewMode, t]);

  if (!canManage) {
    return (
      <SettingsSearchableEntityPage
        title={title}
        subtitle={desc}
        headerAction={<SettingsOfflineHeaderAction show={!backendAvailable} />}
        searchValue=""
        onSearchChange={() => {}}
        searchPlaceholder=""
        loading={false}
        hasItems={true}
        loadingContent={null}
        emptyContent={null}
      >
        <SettingsEntityMessage>
          {t['com.streamlain.settings.workspace.permissions.no-access']?.() ??
            'You need to be an Owner or Admin to manage permissions.'}
        </SettingsEntityMessage>
      </SettingsSearchableEntityPage>
    );
  }

  return (
    <SettingsSearchableEntityPage
      title={title}
      subtitle={desc}
      headerAction={<SettingsOfflineHeaderAction show={!backendAvailable} />}
      searchValue={searchQuery}
      onSearchChange={handleSearchChange}
      searchPlaceholder={searchPlaceholder}
      action={
        <SettingsViewModeMenu
          activeId={viewMode}
          label={viewModeLabel}
          onChange={id => {
            setViewMode(id as ViewMode);
            setSearchQuery('');
          }}
          items={[
            {
              id: 'docs',
              label:
                t[
                  'com.streamlain.settings.workspace.permissions.by-document'
                ]?.() ?? 'By Document',
              icon: <PageIcon />,
            },
            {
              id: 'users',
              label:
                t[
                  'com.streamlain.settings.workspace.permissions.by-user'
                ]?.() ?? 'By User',
              icon: <UserIcon />,
            },
            {
              id: 'groups',
              label:
                t[
                  'com.streamlain.settings.workspace.permissions.by-group'
                ]?.() ?? 'By Group',
              icon: <GroupIcon />,
            },
          ]}
        />
      }
      loading={false}
      hasItems={true}
      loadingContent={null}
      emptyContent={null}
    >
      {viewMode === 'docs' && <DocListView searchQuery={searchQuery} />}
      {viewMode === 'users' && <UserListView searchQuery={searchQuery} />}
      {viewMode === 'groups' && <GroupListView searchQuery={searchQuery} />}
    </SettingsSearchableEntityPage>
  );
};
